import React, { useEffect, useState } from 'react';
import { useRecordContext, usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, Divider, Chip } from '@mui/material';
import moment from 'moment';
import {
  fetchRefundsByPayment,
  logRefundView,
} from '../../services/pendingRefundsService';

const actionLabel = (action, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  switch (action) {
    case 'started':
      return t('payments.log_started', { _: 'İade başlatıldı' });
    case 'scheduled':
      return t('payments.log_scheduled', { _: 'Ertesi güne planlandı' });
    case 'requested':
      return t('payments.log_requested', { _: 'İade başlatıldı (eski kayıt)' });
    case 'processed':
      return t('payments.log_processed', { _: 'İade tamamlandı' });
    case 'failed':
      return t('payments.log_failed', { _: 'İade hatası' });
    case 'viewed':
      return t('payments.log_viewed', { _: 'Görüntülendi' });
    default:
      return action || '—';
  }
};

/**
 * Payment show altında: bağlı iade kayıtları + işlem logları.
 * Sayfa acilinca "viewed" log yazar.
 */
const PaymentRefundRequestLogs = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const paymentId = record?.id;
  const saleId =
    typeof record?.sale === 'string' ? record.sale : record?.sale?.id;

  useEffect(() => {
    if (!paymentId || permissions?.crole !== 'admin') return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        await logRefundView({
          paymentId,
          saleId: saleId || undefined,
          source: 'payment_show',
        });
        const data = await fetchRefundsByPayment(paymentId, saleId || undefined);
        if (!cancelled) setRows(data.data || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paymentId, saleId, permissions]);

  if (permissions?.crole !== 'admin') return null;
  if (!paymentId) return null;
  if (loading && rows.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {translate('payments.loading_logs', { _: 'İade logları yükleniyor…' })}
        </Typography>
      </Box>
    );
  }
  if (!rows.length) return null;

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {translate('payments.refund_logs_title', { _: 'İade logları' })}
      </Typography>
      {rows.map((req) => {
        const logs = Array.isArray(req.logs) ? req.logs : [];
        return (
          <Box
            key={req.id}
            sx={{
              mb: 2,
              p: 2,
              border: '1px solid #eee',
              borderRadius: 1,
              bgcolor: '#fafafa',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">
                {req.eventName || 'İade kaydı'}
              </Typography>
              <Chip size="small" label={req.status || '—'} />
              {req.ticketPnr && (
                <Typography variant="caption" color="text.secondary">
                  PNR: {req.ticketPnr}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Başlatan: {req.createdby || req.userEmail || '—'}
              {req.processedBy
                ? ` · İade eden: ${req.processedBy}`
                : ''}
            </Typography>
            {req.lastError && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                Son hata: {req.lastError}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
              İşlem geçmişi
            </Typography>
            {logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Log yok
              </Typography>
            ) : (
              [...logs].reverse().map((entry, idx) => (
                <Box
                  key={`${entry.at}-${idx}`}
                  sx={{
                    py: 0.75,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Typography variant="body2">
                    <strong>{actionLabel(entry.action, translate)}</strong>
                    {' — '}
                    {entry.at ? moment(entry.at).format('LLL') : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Kim: {entry.actor || '—'}
                  </Typography>
                  {entry.detail && (
                    <Typography variant="body2">{entry.detail}</Typography>
                  )}
                  {entry.error && (
                    <Typography variant="body2" color="error">
                      Hata: {entry.error}
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default PaymentRefundRequestLogs;
